import { BehaviorSubject, Subject, tap } from "rxjs";
import { ApiService } from "../services/api.service";
import { QQQuestionnairePageViewModel } from "./qq-questionnaire-page.view-model";
import { QuestionnaireModel } from "./questionnaire.model";

export class QQQuestionnaireViewModel {
  initialScreen: boolean = true;
  id: string;
  quoteKey: string;
  name: string;
  header: string;
  multiple: boolean;
  pages: QQQuestionnairePageViewModel[] = [];
  currentPageIndex: number;
  answers = new Map<string, string>();
  answersObject: any = {};
  summary: boolean = false;
  complete: boolean = false;
  visiblePages$ = new BehaviorSubject<QQQuestionnairePageViewModel[]>([]);
  currentPage$ = new BehaviorSubject<QQQuestionnairePageViewModel>(null);
  answerSaved$ = new Subject<{ key: string, value: string }>();
  saving: Promise<void> = null;
  sourceUrl: string;

  get currentPage() { return this.currentPage$.value; }

  constructor(
    public questionnaire: QuestionnaireModel,
    public api: ApiService,
    answers?: any,
    quoteKey?: string
  ) {
    this.id = questionnaire.id;
    this.name = questionnaire.name;
    this.header = questionnaire.header;
    this.multiple = questionnaire.multiple;
    this.pages = questionnaire.pages.map((x, i) => new QQQuestionnairePageViewModel(x, api, this, i));
    if (quoteKey)
      this.quoteKey = quoteKey;

    if (answers) {
      for (const key in answers) {
        this.saveAnswer(key, answers[key]);
      }
    }

    for (const page of this.pages) {
      page.onNext.subscribe(_ => this.next(page));
      page.onPrev.subscribe(_ => this.back());
      page.onQuestionAnswered.pipe(
        tap(({ question, answer }) => {
          this.saveAnswer(question.key, answer);
          this.checkVisibility();
        })
      ).subscribe();

      for (const q of page.questions) {
        if (!this.answersObject[q.key])
          this.answersObject[q.key] = q.answer;
      }
    };

    this.checkVisibility();

    this.currentPage$.next(this.pages[0]);
    this.currentPageIndex = 0;
  }

  next(page: QQQuestionnairePageViewModel) {
    page.questions.forEach(x => {
      this.saveAnswer(x.key, x.answer)
    });

    this.checkVisibility();

    let pageCount = this.pages.length;
    let index = this.pages.indexOf(page);
    let nextPage: QQQuestionnairePageViewModel;
    let nextIndex: number;

    for (let i = index + 1; i < this.pages.length; i++) {
      nextPage = this.pages[i];
      if (nextPage.visible) {
        nextIndex = i;
        break;
      }
    }

    if (nextIndex < pageCount) {
      this.currentPage$.next(nextPage);
      this.currentPageIndex = this.currentPageIndex + 1;
      return true;
    }

    return false;
  }

  back(pageCount = 1) {
    if (this.currentPageIndex <= 0)
      return false;

    this.goToPage(this.currentPageIndex - pageCount);
    return true;
  }

  getPage(pageIndex: number) {
    return this.pages[pageIndex];
  }

  goToPage(pageIndex: number) {
    this.currentPageIndex = Math.min(Math.max(0, pageIndex), this.visiblePages$.value.length - 1);
    this.currentPage$.next(this.visiblePages$.value[this.currentPageIndex]);
  }

  checkVisibility() {
    this.pages.forEach(x => x.checkVisibility(this.answersObject));
    this.visiblePages$.next(this.pages.filter(x => x.visible));
  }

  saveAnswer(key: string, value: string) {
    this.answers.set(key, value);
    this.answersObject[key] = value;
    this.pages.forEach(p => p.questions.forEach(q => {
      if (q.key === key)
        q.answer = value;
    }));

    this.answerSaved$.next({ key, value });
  }

  answerText(key: string) {
    const value = this.answersObject[key];
    for (let p of this.pages) {
      if (!p.visible)
        continue;

      for (let q of p.questions) {
        if (!q.visible)
          continue;

        if (q.key === key) {
          return q.options.find((x: any) => x.Value == value)?.Label;
        }
      }
    }
    return value;
  }
}
